import React, { useState, useEffect, useCallback } from 'react';
import ProductImage from '@/components/brand/ProductImage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { MARKETPLACE_FILTER_SLUGS, formatPriceNaira } from '@/lib/catalog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Filter, Package, Loader2, CheckCircle2, Factory, HelpCircle } from 'lucide-react';

const CATEGORY_KEYS = MARKETPLACE_FILTER_SLUGS;

// Products created in the app store manufacturer_name; older seed data
// used manufacturer.
// Products per page; "Show more products" loads the next page.
const PAGE_SIZE = 48;

const manufacturerOf = (product) => product?.manufacturer_name || product?.manufacturer || '';

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Questions about a product go to the African Farm support team, with
  // the product attached (chats between users were removed in PR 56).
  const askLabel = t('askShopkeeper.title', { defaultValue: 'Ask about this product' });
  const askSupport = (product) => navigate('/support/chat', {
    state: { product: { id: product.id, name: product.name } },
  });
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed search / category / selected product id from the URL on the
  // first render so deep links from home-screen tiles land where
  // they're supposed to. The URL stays the shared source of truth —
  // every filter / search / product-open writes back to it so a tab
  // refresh keeps context and a shared link reproduces the view.
  const urlSearch = searchParams.get('search') || '';
  const urlCategory = CATEGORY_KEYS.includes(searchParams.get('category'))
    ? searchParams.get('category')
    : 'all';
  const urlProduct = searchParams.get('product') || null;

  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(urlSearch);
  const [category, setCategory] = useState(urlCategory);
  const [selected, setSelected] = useState(null);

  const _syncUrl = useCallback(
    (next) => {
      // react-router merges, so pass only the keys we own. `undefined`
      // deletes a param — keeps the URL tidy when the user clears a
      // filter.
      const current = Object.fromEntries(searchParams.entries());
      const merged = { ...current, ...next };
      Object.keys(merged).forEach((k) => {
        if (merged[k] == null || merged[k] === '' || merged[k] === 'all') delete merged[k];
      });
      setSearchParams(merged, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const queryParams = (opts = {}) => {
    const effectiveCategory = opts.category ?? category;
    const effectiveSearch = opts.search ?? search;
    const params = { limit: PAGE_SIZE };
    if (effectiveCategory && effectiveCategory !== 'all') params.category = effectiveCategory;
    if (effectiveSearch) params.search = effectiveSearch;
    return params;
  };

  const loadProducts = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const res = await api.get('/marketplace/products', { params: queryParams(opts) });
      const rows = res.data || [];
      setProducts(rows);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load products', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search]);

  // Next page, appended. Skips any id already shown in case the
  // catalogue changed between pages.
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get('/marketplace/products', {
        params: { ...queryParams(), offset: products.length },
      });
      const rows = res.data || [];
      setProducts((current) => {
        const seen = new Set(current.map((p) => p.id));
        return [...current, ...rows.filter((p) => !seen.has(p.id))];
      });
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more products', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Initial load + reload on category change (search reloads only on
  // explicit submit — typing shouldn't fire a request per keystroke).
  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Open the detail dialog when the URL carries `?product=<id>`. Needs
  // to happen whenever the id changes AFTER mount so an in-app
  // navigation (not just a reload) does the right thing.
  useEffect(() => {
    if (!urlProduct) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    api.get(`/marketplace/products/${urlProduct}`)
      .then((res) => { if (!cancelled && res?.data) setSelected(res.data); })
      .catch(() => { /* Dialog just stays closed on 404 / network error. */ });
    return () => { cancelled = true; };
  }, [urlProduct]);

  const handleSearch = (e) => {
    e.preventDefault();
    _syncUrl({ search });
    loadProducts({ search });
  };

  const handleCategoryChange = (next) => {
    setCategory(next);
    _syncUrl({ category: next });
  };

  const openProduct = (product) => {
    setSelected(product);
    _syncUrl({ product: product?.id });
  };

  const closeProduct = () => {
    setSelected(null);
    _syncUrl({ product: undefined });
  };

  const renderProducts = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (products.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('marketplace.empty.title')}</h3>
            <p className="text-muted-foreground">{t('marketplace.empty.subtitle')}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((product) => (
          <Card
            key={product.id}
            role="button"
            tabIndex={0}
            onClick={() => openProduct(product)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProduct(product); } }}
            className="hover:shadow-farm-lg transition-shadow overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-farm-primary"
            data-testid={`product-card-${product.id}`}
          >
            <div className="h-40 overflow-hidden">
              <ProductImage product={product} />
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold line-clamp-1">{product.name}</h3>
                {product.verified && (
                  <span className="bg-farm-success/10 text-farm-success text-xs px-2 py-1 rounded-full">{t('marketplace.verifiedBadge')}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{product.description}</p>
              <div className="flex items-center justify-between">
                <p className="font-bold text-lg text-farm-primary">
                  {formatPriceNaira(product, t('marketplace.priceUnavailable', { defaultValue: 'N/A' }))}
                </p>
                <span className="text-xs text-muted-foreground">{t(`products.categories.${product.category}`, product.category)}</span>
              </div>
              {manufacturerOf(product) && (
                <p className="text-xs text-muted-foreground mt-2">{t('marketplace.by', { name: manufacturerOf(product) })}</p>
              )}
              <Button
                className="w-full mt-4 bg-farm-secondary hover:bg-farm-secondary-light"
                size="sm"
                onClick={(e) => { e.stopPropagation(); askSupport(product); }}
                data-testid={`ask-shopkeeper-card-${product.id}`}
              >
                <HelpCircle className="w-4 h-4 mr-2" /> {askLabel}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderDetail = () => {
    if (!selected) return null;
    return (
      <Dialog open={!!selected} onOpenChange={(open) => !open && closeProduct()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-start justify-between gap-3">
              <span>{selected.name}</span>
              {selected.verified && (
                <span className="inline-flex items-center gap-1 bg-farm-success/10 text-farm-success text-xs px-2 py-1 rounded-full whitespace-nowrap">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {t('marketplace.verifiedBadge')}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="h-48 rounded-lg overflow-hidden">
              <ProductImage product={selected} />
            </div>
            <div className="flex items-center justify-between">
              <p className="font-bold text-2xl text-farm-primary">
                {formatPriceNaira(selected, t('marketplace.priceUnavailable', { defaultValue: 'N/A' }))}
              </p>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                {t(`products.categories.${selected.category}`, selected.category)}
              </span>
            </div>
            {selected.description && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{t('marketplace.detail.description')}</p>
                <p className="text-sm whitespace-pre-line">{selected.description}</p>
              </div>
            )}
            {selected.active_ingredient && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{t('marketplace.detail.activeIngredient')}</p>
                <p className="text-sm">{selected.active_ingredient}</p>
              </div>
            )}
            {selected.dosage && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{t('marketplace.detail.dosage')}</p>
                <p className="text-sm whitespace-pre-line">{selected.dosage}</p>
              </div>
            )}
            {selected.instructions && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{t('marketplace.detail.instructions')}</p>
                <p className="text-sm whitespace-pre-line">{selected.instructions}</p>
              </div>
            )}
            {manufacturerOf(selected) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Factory className="w-4 h-4" />
                {t('marketplace.by', { name: manufacturerOf(selected) })}
              </div>
            )}
            <Button
              className="w-full bg-farm-secondary hover:bg-farm-secondary-light"
              onClick={() => askSupport(selected)}
              data-testid="ask-shopkeeper-detail"
            >
              <HelpCircle className="w-4 h-4 mr-2" /> {askLabel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('marketplace.title')}</h1>
        <p className="text-muted-foreground">{t('marketplace.subtitle')}</p>
      </div>

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder={t('marketplace.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={t('marketplace.categoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>{t(`marketplace.categories.${key}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-farm-primary hover:bg-farm-primary-dark">
              {t('marketplace.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {renderProducts()}
      {!loading && hasMore && (
        <div className="flex justify-center mt-6">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="marketplace-load-more"
          >
            {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('marketplace.loadMore', { defaultValue: 'Show more products' })}
          </Button>
        </div>
      )}
      {renderDetail()}
    </div>
  );
}
